import {
  FEATURES,
  IGroupInfo,
  ImmutableChat,
  ISendMessagePayload,
  SEND_MESSAGE_TYPE,
} from "@home-health-notify/domain";
import ArticleIcon from "@mui/icons-material/Article";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import QuickreplyIcon from "@mui/icons-material/Quickreply";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import BiotechIcon from "@mui/icons-material/Biotech";
import { SpeedDialAction, SpeedDialIcon } from "@mui/material";
import SpeedDial from "@mui/material/SpeedDial";
import React, { useContext, useState } from "react";
import { ConditionalMapsApiProvider } from "../../common/ConditionalMapsApiProvider";
import { TakePhotoModal } from "../../header/userProfile/TakePhotoModal";
import theme from "../../theme";
import QuickReplyModal from "./QuickReply/QuickReplyModal";
import Reminder from "./Reminder";
import ShareLocation from "./ShareLocation";
import TemplateAccess from "./TemplateAccess";
import { LeftDrawerContext } from "../../context/LeftDrawerContext";
import NewLabRequisition from "../../labs/NewLabRequisition";
import { userTracking } from "../../analytics/userTracking";
import { LabRequisitionsProvider, OrganizationContext } from "hhn-commons";

const SpeedDialComponent = ({
  chat,
  selectFile,
  open,
  onOpen,
  onClose,
  sendMessage,
  isPriority,
  setPriority,
}: {
  chat: ImmutableChat;
  selectFile: () => void;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  sendMessage(
    payload: ISendMessagePayload,
    sendMessageType: SEND_MESSAGE_TYPE,
  ): void;
  isPriority: boolean;
  setPriority: (isPriority: boolean) => void;
}) => {
  const { openDrawer } = useContext(LeftDrawerContext);
  const { selectedOrgUser } = useContext(OrganizationContext);
  const isPatientGroup = chat.isPatientGroup();
  const canSendTemplateMessage =
    isPatientGroup &&
    !!selectedOrgUser?.hasAccess(FEATURES.SEND_TEMPLATE_MESSAGE);

  type ActionInput = {
    icon: React.ElementType;
    name: string;
    fabProps: {
      disabled: boolean;
    };
  };

  const isLabsEnableLabs =
    selectedOrgUser?.isLabIntegrationEnabled() &&
    selectedOrgUser?.hasAccess(FEATURES.ACCESS_LAB_REQUISITIONS);

  const actions: ActionInput[] = [
    ...(isLabsEnableLabs
      ? [
          {
            icon: BiotechIcon,
            name: "Lab Orders",
            fabProps: { disabled: false },
          },
        ]
      : []),
    {
      icon: AttachFileIcon,
      name: "Attach File",
      fabProps: { disabled: false },
    },
    { icon: WatchLaterIcon, name: "Reminder", fabProps: { disabled: false } },
    ...(canSendTemplateMessage
      ? [
          {
            icon: ArticleIcon,
            name: "Template Message",
            fabProps: { disabled: false },
          },
        ]
      : []),
    {
      icon: QuickreplyIcon,
      name: "Quick Reply",
      fabProps: { disabled: false },
    },
    {
      icon: CameraAltIcon,
      name: "Get Snapshot",
      fabProps: { disabled: true },
    },
    {
      icon: LocationOnIcon,
      name: "Share Location",
      fabProps: { disabled: false },
    },
  ];

  const onAction = (actionName: string) => {
    // Track Speed Dial action click
    const chatType = chat.isPatientGroup()
      ? "patient_group"
      : chat.getChatType();
    userTracking.trackSpeedDialAction(actionName, chatType);

    onOpen();
    if (actionName === "Attach File") {
      selectFile();
    } else if (actionName === "Reminder") {
      onOpenReminderDialog();
    } else if (actionName === "Template Message") {
      onOpenTemplateAccessDialog();
    } else if (actionName === "Share Location") {
      onOpenShareLocationDialog();
    } else if (actionName === "Get Snapshot") {
      onOpenGetSnapshotDialog();
    } else if (actionName === "Quick Reply") {
      onOpenQuickReplyDialog();
    } else if (actionName === "Lab Orders") {
      onLabRequisitions();
    }
  };

  const [isReminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [isTemplateAccessDialogOpen, setTemplateAccessDialogOpen] =
    useState(false);
  const [isShareLocationDialogOpen, setIsShareLocationDialogOpen] =
    useState<boolean>(false);
  const [isGetSnapshotDialogOpen, setIsGetSnapshotDialogOpen] =
    useState<boolean>(false);
  const [isQuickReplyDialogOpen, setIsQuickReplyDialogOpen] =
    useState<boolean>(false);

  const onOpenReminderDialog = () => {
    setReminderDialogOpen(true);
  };

  const onCloseReminderDialog = () => {
    setReminderDialogOpen(false);
  };

  const onOpenTemplateAccessDialog = () => {
    setTemplateAccessDialogOpen(true);
  };

  const onCloseTemplateAccessDialog = () => {
    setTemplateAccessDialogOpen(false);
  };

  const onOpenShareLocationDialog = () => {
    setIsShareLocationDialogOpen(true);
  };

  const onCloseShareLocationDialog = () => {
    setIsShareLocationDialogOpen(false);
  };

  const onOpenGetSnapshotDialog = () => {
    setIsGetSnapshotDialogOpen(true);
  };

  const onCloseGetSnapshotDialog = () => {
    setIsGetSnapshotDialogOpen(false);
  };

  const onOpenQuickReplyDialog = () => {
    setIsQuickReplyDialogOpen(true);
  };

  const onLabRequisitions = () => {
    openDrawer(
      <LabRequisitionsProvider>
        <NewLabRequisition
          groupInfo={chat.chatInfo as IGroupInfo}
          readonly={false}
        ></NewLabRequisition>
      </LabRequisitionsProvider>,
    );
  };

  const onCloseQuickReplyDialog = () => {
    setIsQuickReplyDialogOpen(false);
  };

  const handleOnClick = () => {
    if (open) onClose();
    else onOpen();
  };

  return (
    <>
      <SpeedDial
        ariaLabel="SpeedDial tooltip example"
        sx={{
          position: "absolute",
          bottom: 16,
          width: 40,
          left: "inherit",
        }}
        icon={
          <SpeedDialIcon
            sx={{
              height: "30px",
            }}
          />
        }
        FabProps={{
          size: "small",
          sx: {
            boxShadow: "none",
            "& .MuiSpeedDialIcon-icon": {
              fontSize: 30,
            },
          },
        }}
        open={open}
        onClick={handleOnClick}
        data-testid="speed-dial-icon"
      >
        {actions.map(action => {
          const Icon: React.ElementType = action.icon;
          return (
            <SpeedDialAction
              key={action.name}
              icon={<Icon sx={{ height: 30, width: 30 }} />}
              onClick={() => onAction(action.name)}
              slotProps={{
                fab: {
                  ...action.fabProps,
                },
                tooltip: {
                  title: action.name,
                  placement: "right",
                },
              }}
              sx={{
                height: 50,
                width: 50,
                background: theme.palette.common.white,
              }}
            />
          );
        })}
      </SpeedDial>
      <Reminder
        sendMessage={sendMessage}
        isOpen={isReminderDialogOpen}
        onClose={onCloseReminderDialog}
      />
      <TemplateAccess
        chat={chat}
        isOpen={isTemplateAccessDialogOpen}
        onClose={onCloseTemplateAccessDialog}
      />
      {isShareLocationDialogOpen && (
        <ConditionalMapsApiProvider>
          <ShareLocation
            isOpen={isShareLocationDialogOpen}
            onClose={onCloseShareLocationDialog}
            sendMessage={sendMessage}
          />
        </ConditionalMapsApiProvider>
      )}
      <TakePhotoModal
        open={isGetSnapshotDialogOpen}
        closeModal={onCloseGetSnapshotDialog}
      />
      <QuickReplyModal
        open={isQuickReplyDialogOpen}
        closeModal={onCloseQuickReplyDialog}
        sendMessage={sendMessage}
        isPriority={isPriority}
        setPriority={setPriority}
      />
    </>
  );
};

export default SpeedDialComponent;
